import { getAccessToken, refreshAccessToken as defaultRefreshAccessToken } from "./tokenStorage";

// Same backoff ladder as centralMonitor.js's old REST-retry logic.
const BACKOFF_DELAYS_MS = [5, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600].map(
    (seconds) => seconds * 1000
);

// Close codes documented in the telemetry payload spec (v2.1), §2.4.
export const KEYAR_CLOSE_CODES = {
    INVALID_PATIENT_ID: 4400,
    UNAUTHENTICATED: 4401,
    UNAUTHORIZED: 4403,
    // Server-side 30s device-inactivity auto-close (spec §2.4/§8.7). Not a
    // failure: it just means nothing was streaming. Reconnect promptly.
    INACTIVITY: 4408,
    // Ours: we deliberately recycle a connection that has delivered nothing
    // for a while (see idleRecycleMs). Not a failure.
    CLIENT_RECYCLE: 4000,
};

// Upper bound on how long a refresh-token call may hold up the reconnect.
// Without it a hung request would mean we never schedule another attempt.
const REFRESH_TIMEOUT_MS = 10 * 1000;
// With alwaysRetry, how often to retry after a normally-permanent close.
const PERMANENT_CLOSE_RETRY_MS = 60 * 1000;

// 4400/4403 can never succeed here, so they're not retried.
// 4401 and standard closes are retried; reconnect re-reads the token.
const NON_RETRYABLE_CODES = new Set([
    KEYAR_CLOSE_CODES.INVALID_PATIENT_ID,
    KEYAR_CLOSE_CODES.UNAUTHORIZED,
]);

/**
 * @param {() => string} buildUrl - returns the base ws(s) URL + route, without the token query param.
 * @param {(frame: object) => void} onMessage - called with each parsed JSON frame.
 * @param {(status: 'connecting'|'open'|'reconnecting'|'error'|'closed', detail?: object) => void} [onStatusChange]
 * @param {() => string} [getToken] - re-read on every connect attempt.
 * @param {() => Promise<string|null>} [refreshAccessToken] - called on a 4401 close, before reconnecting.
 * @param {typeof WebSocket} [WebSocketImpl] - injectable for testing.
 * @param {number} [maxDelayMs] - upper bound on the backoff ladder. A place-wide
 *   socket should keep retrying quickly (a new patient can start at any time);
 *   the default leaves the full ladder in place.
 * @param {number} [idleRecycleMs] - if an open connection delivers no frame for this
 *   long, close it and reconnect. There is no client heartbeat in the protocol, so
 *   this is how a half-dead connection, or one that missed a newly-started session,
 *   gets replaced. Default: never.
 * @param {boolean} [alwaysRetry] - keep retrying (slowly) even after 4400/4403, which
 *   are otherwise permanent. For a place-wide viewer that must self-heal.
 */
export function createKeyarSocket({
    buildUrl,
    onMessage,
    onStatusChange = () => {},
    getToken = () => getAccessToken(),
    refreshAccessToken = defaultRefreshAccessToken,
    WebSocketImpl = typeof window !== "undefined" ? window.WebSocket : undefined,
    maxDelayMs = Infinity,
    idleRecycleMs = Infinity,
    alwaysRetry = false,
}) {
    let socket = null;
    let closedByCaller = false;
    let delayIndex = 0;
    let reconnectTimer = null;
    // Set from a 4401's refresh response and consumed by the very next
    // connect() attempt — mirrors authInterceptors.js's REST retry, which
    // uses the refreshed token straight from the response instead of
    // re-reading storage. Re-reading storage here would depend on
    // setTokens() having already persisted the cookie, which isn't
    // guaranteed to have happened yet (and can silently fail, e.g. a
    // Secure cookie on a non-HTTPS origin) by the time this runs.
    let pendingToken = null;
    // True from the moment a connection attempt starts until it closes.
    let active = false;
    let listening = false;
    let idleTimer = null;

    const clearIdle = () => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = null;
    };
    // (Re)start the "nothing received" countdown for this socket.
    const armIdle = (sock) => {
        clearIdle();
        if (idleRecycleMs === Infinity) return;
        idleTimer = setTimeout(() => {
            if (socket !== sock) return;
            console.info("[KEYAR-WS] recycling idle connection", { url: buildUrl(), idleMs: idleRecycleMs });
            sock.close(KEYAR_CLOSE_CODES.CLIENT_RECYCLE, "idle-recycle");
        }, idleRecycleMs);
    };

    const scheduleReconnect = () => {
        const delay = Math.min(
            BACKOFF_DELAYS_MS[Math.min(delayIndex, BACKOFF_DELAYS_MS.length - 1)],
            maxDelayMs
        );
        delayIndex = Math.min(delayIndex + 1, BACKOFF_DELAYS_MS.length - 1);
        onStatusChange("reconnecting", { delay });
        reconnectTimer = setTimeout(connect, delay);
    };

    // A backgrounded tab or a dropped network can leave us sitting on a long
    // backoff timer; when the tab is visible / the network is back, retry now.
    const reconnectNow = () => {
        if (closedByCaller || active) return;
        if (reconnectTimer) clearTimeout(reconnectTimer);
        reconnectTimer = null;
        delayIndex = 0;
        connect();
    };
    const onVisibility = () => {
        if (typeof document !== "undefined" && document.visibilityState === "visible") reconnectNow();
    };

    function connect() {
        if (closedByCaller) return;
        reconnectTimer = null;
        if (!listening && typeof window !== "undefined") {
            listening = true;
            window.addEventListener("online", reconnectNow);
            document.addEventListener("visibilitychange", onVisibility);
        }

        const token = pendingToken || getToken();
        pendingToken = null;
        if (!token) {
            // No token to connect with at all (e.g. logged out, or the
            // brief window between clearTokens() and the resulting
            // redirect actually navigating away) — connecting now would
            // just produce a doomed ?token=undefined request. Wait on the
            // normal backoff ladder instead; if a token reappears later
            // (or the page is about to unload anyway) this resolves
            // itself without ever hitting the network.
            scheduleReconnect();
            return;
        }
        const url = `${buildUrl()}?token=${token}`;
        active = true;
        onStatusChange("connecting");

        // A real app-level close code for a bad token (4401) can only ever
        // arrive after the server accepts the handshake. If it instead
        // rejects the handshake itself (e.g. a plain HTTP 403 on the
        // upgrade request for an invalid/expired token), the browser can't
        // surface that status at all — it just reports a generic abnormal
        // closure (1006) to onclose, indistinguishable from a real network
        // blip by code alone. didOpen lets onclose tell "never even
        // connected" apart from "connected, then dropped".
        let didOpen = false;
        socket = new WebSocketImpl(url);

        socket.onopen = () => {
            didOpen = true;
            delayIndex = 0;
            armIdle(socket);
            onStatusChange("open");
        };

        socket.onmessage = (event) => {
            armIdle(socket);
            let frame;
            try {
                frame = JSON.parse(event.data);
            } catch (err) {
                return;
            }
            onMessage(frame);
        };

        socket.onclose = (event) => {
            active = false;
            clearIdle();
            console.info("[KEYAR-WS] closed", { url: buildUrl(), code: event.code, reason: event.reason, wasOpen: didOpen });
            if (closedByCaller) {
                onStatusChange("closed");
                return;
            }
            if (NON_RETRYABLE_CODES.has(event.code)) {
                onStatusChange("error", { code: event.code });
                if (alwaysRetry) {
                    reconnectTimer = setTimeout(connect, PERMANENT_CLOSE_RETRY_MS);
                }
                return;
            }
            if (
                (event.code === KEYAR_CLOSE_CODES.INACTIVITY ||
                    event.code === KEYAR_CLOSE_CODES.CLIENT_RECYCLE) &&
                didOpen
            ) {
                // Idle auto-close (ours or the server's), not a failure: no
                // token refresh, no backoff climb — just come back shortly.
                delayIndex = 0;
                onStatusChange("reconnecting", { delay: 1000 });
                reconnectTimer = setTimeout(connect, 1000);
                return;
            }
            if (event.code === KEYAR_CLOSE_CODES.UNAUTHENTICATED || !didOpen) {
                // The stale token that just got this connection rejected
                // would get rejected again on a plain reconnect — refresh
                // it first so the next attempt has a shot at succeeding.
                // Use the refreshed value directly (see pendingToken above)
                // rather than trusting it's already in storage by the time
                // the next connect() runs. Covers the handshake-rejection
                // case above too, not just an explicit 4401 — a connection
                // that never opened at all is treated the same way, since
                // a bad token is a plausible cause and refreshing again
                // when it wasn't the cause is harmless (the refresh
                // endpoint just gets called once per failed attempt).
                // A refresh call that never settles must not stop us
                // reconnecting, so whichever of {refresh result, timeout}
                // comes first schedules the reconnect (exactly once).
                let proceeded = false;
                const proceed = (newToken) => {
                    if (proceeded) return;
                    proceeded = true;
                    clearTimeout(refreshTimer);
                    if (newToken) pendingToken = newToken;
                    scheduleReconnect();
                };
                const refreshTimer = setTimeout(() => proceed(null), REFRESH_TIMEOUT_MS);
                refreshAccessToken().then(proceed, () => proceed(null));
                return;
            }
            scheduleReconnect();
        };
    }

    const close = () => {
        closedByCaller = true;
        if (listening) {
            window.removeEventListener("online", reconnectNow);
            document.removeEventListener("visibilitychange", onVisibility);
            listening = false;
        }
        if (reconnectTimer) clearTimeout(reconnectTimer);
        clearIdle();
        if (socket) socket.close();
    };

    return { connect, close };
}
